/**
 * React Query hooks for training material attachments
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../lib/apiClient";

export interface TrainingAttachment {
  id: string;
  material_id: string;
  file_name: string;
  file_type: "video" | "audio" | "pdf" | "ppt" | "doc" | "image" | "other";
  content_type: string;
  file_size: number | null;
  s3_bucket: string;
  s3_key: string;
  s3_region: string;
  display_order: number;
  title: string | null;
  description: string | null;
  duration_seconds: number | null;
  uploaded_by: string | null;
  created_at: string;
  updated_at: string;
  download_url?: string; // Presigned URL (only in GET single)
}

export interface CreateAttachmentData {
  file?: File;
  s3_key?: string;
  content_type?: string;
  file_type?: string;
  title?: string;
  description?: string;
  display_order?: number;
  duration_seconds?: number;
}

export interface UpdateAttachmentData {
  title?: string;
  description?: string;
  display_order?: number;
  duration_seconds?: number;
}

// ============================================================================
// Query: Get all attachments for a training material
// ============================================================================

export function useTrainingAttachments(materialId: string) {
  return useQuery({
    queryKey: ["training-attachments", materialId],
    queryFn: async () => {
      const response = await apiClient.get<{
        data: { attachments: TrainingAttachment[] };
      }>(`/admin/training-materials/${materialId}/attachments`);
      return response.data.data.attachments || [];
    },
    enabled: !!materialId,
  });
}

// ============================================================================
// Query: Get single attachment with presigned download URL
// ============================================================================

export function useTrainingAttachment(
  materialId: string,
  attachmentId: string,
) {
  return useQuery({
    queryKey: ["training-attachment", materialId, attachmentId],
    queryFn: async () => {
      const response = await apiClient.get<{
        data: { attachment: TrainingAttachment };
      }>(`/admin/training-materials/${materialId}/attachments/${attachmentId}`);
      return response.data.data.attachment;
    },
    enabled: !!materialId && !!attachmentId,
  });
}

// ============================================================================
// Mutation: Upload new attachment
// ============================================================================

export function useCreateAttachment(materialId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    // US-081: error fallback centralized here; TrainingMaterialEditPage catch is sole caller.
    meta: { errorFallback: "Failed to upload attachment" },
    mutationFn: async (data: CreateAttachmentData) => {
      const formData = new FormData();

      if (data.file) {
        formData.append("file", data.file);
      }
      if (data.s3_key) {
        formData.append("s3_key", data.s3_key);
      }
      if (data.content_type) {
        formData.append("content_type", data.content_type);
      }
      if (data.file_type) {
        formData.append("file_type", data.file_type);
      }
      if (data.title) {
        formData.append("title", data.title);
      }
      if (data.description) {
        formData.append("description", data.description);
      }
      if (data.display_order !== undefined) {
        formData.append("display_order", data.display_order.toString());
      }
      if (data.duration_seconds !== undefined) {
        formData.append("duration_seconds", data.duration_seconds.toString());
      }

      const response = await apiClient.post<{
        data: { attachment: TrainingAttachment };
      }>(`/admin/training-materials/${materialId}/attachments`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data.data.attachment;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["training-attachments", materialId],
      });
    },
  });
}

// ============================================================================
// Mutation: Update attachment metadata
// ============================================================================

export function useUpdateAttachment(materialId: string, attachmentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateAttachmentData) => {
      const response = await apiClient.put<{
        data: { attachment: TrainingAttachment };
      }>(
        `/admin/training-materials/${materialId}/attachments/${attachmentId}`,
        data,
      );
      return response.data.data.attachment;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["training-attachments", materialId],
      });
      queryClient.invalidateQueries({
        queryKey: ["training-attachment", materialId, attachmentId],
      });
    },
  });
}

// ============================================================================
// Mutation: Delete attachment
// ============================================================================

export function useDeleteAttachment(materialId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    // US-081: error fallback centralized here; TrainingMaterialEditPage catch is sole caller.
    meta: { errorFallback: "Failed to delete attachment" },
    mutationFn: async (attachmentId: string) => {
      await apiClient.delete(
        `/admin/training-materials/${materialId}/attachments/${attachmentId}`,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["training-attachments", materialId],
      });
    },
  });
}

// ============================================================================
// Helper: Get icon for file type
// ============================================================================

export function getFileTypeIcon(
  fileType: TrainingAttachment["file_type"],
): string {
  const iconMap = {
    video: "video",
    audio: "volume",
    pdf: "file-text",
    ppt: "presentation",
    doc: "file-text",
    image: "photo",
    other: "file",
  };
  return iconMap[fileType] || "file";
}

// ============================================================================
// Helper: Format file size
// ============================================================================

export function formatFileSize(bytes: number | null): string {
  if (!bytes) return "Unknown size";

  const units = ["B", "KB", "MB", "GB"];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${size.toFixed(1)} ${units[unitIndex]}`;
}

// ============================================================================
// Helper: Format duration
// ============================================================================

export function formatDuration(seconds: number | null): string {
  if (!seconds) return "";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
}
